import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Popper,
  Stack,
  styled,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Autocomplete } from "src/components";
import { useSession } from "src/hooks";
import { TaskItemDetailDto, useEditTaskItemTeam, useGetTeams } from "src/http";
import { ITeamSummary } from "src/models";

const MenuItem = styled(Box)`
  &:first-child {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  }

  &:last-child {
    margin-bottom: 2px;
  }
` as typeof Box;

interface Props {
  open: boolean;
  taskItem: TaskItemDetailDto;
  onUpdated: (selectedTeam: ITeamSummary | null) => void;
  onCancelled: () => void;
}

const EditTaskItemTeamModal = (props: Props) => {
  const { userId: sessionUserId } = useSession();
  const { data: teams, isFetching: isFetchingTeams } = useGetTeams(sessionUserId, props.open);
  const { mutate: editTaskItemTeam, isLoading: isEditingTeam } = useEditTaskItemTeam();
  const theme = useTheme();
  const [selectedTeam, setSelectedTeam] = useState<ITeamSummary | null>(null);

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.taskItem]);

  function reset() {
    if (props.taskItem.team) {
      setSelectedTeam(teams?.find((x) => x.slug === props.taskItem.team?.slug) ?? null);
    } else {
      setSelectedTeam(null);
    }
  }

  function handleCancelClicked() {
    reset();
    props.onCancelled();
  }

  function handleDoneClicked() {
    editTaskItemTeam(
      {
        taskItemGuid: props.taskItem.guid,
        newTeam: selectedTeam,
        oldTeamSlug: props.taskItem.team?.slug,
        ownerUserGuids: props.taskItem.owners.map((x) => x.user.userId),
      },
      {
        onSuccess: () => props.onUpdated(selectedTeam),
      }
    );
  }

  function sortFunction(a: ITeamSummary, b: ITeamSummary) {
    let aMembership = a.isAdministrator || a.isMember;
    let bMembership = b.isAdministrator || b.isMember;

    if (aMembership !== bMembership) {
      return aMembership ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  }

  return (
    <Dialog open={props.open} onClose={handleCancelClicked}>
      <DialogTitle>Change Team</DialogTitle>
      <DialogContent>
        <Typography variant="label">Team</Typography>
        <Autocomplete
          isLoading={isFetchingTeams}
          groupBy={(option) => (option.isMember || option.isAdministrator ? "My Teams" : "Other Teams")}
          options={teams ? [...teams].sort(sortFunction) : []}
          getOptionLabel={(option: ITeamSummary) => option.name}
          renderGroup={(group) => (
            <Box key={group.key} sx={{ pb: 0.25 }}>
              <Stack
                spacing={0.2}
                sx={{ px: 0.5, pt: 0.75, position: "sticky", top: 0, backgroundColor: theme.palette.common.white }}
              >
                <Typography variant="caption">{group.group.toLocaleUpperCase()}</Typography>
                <Divider />
              </Stack>
              {group.children}
            </Box>
          )}
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.slug} component="li">
              <Stack>
                <Typography>{option.name}</Typography>
              </Stack>
            </MenuItem>
          )}
          isOptionEqualToValue={(option, value) => option.slug === value.slug}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select a team" inputProps={{ ...params.inputProps }} />
          )}
          onChange={(_event, value) => setSelectedTeam(value)}
          PopperComponent={(props) => (
            <Popper {...props} style={{ ...props.style, margin: 0, padding: 0, minWidth: "300px" }} />
          )}
          value={selectedTeam}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isEditingTeam} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isEditingTeam}
          endIcon={isEditingTeam ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleDoneClicked}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { EditTaskItemTeamModal };
